import { Injectable } from "@angular/core";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { Observable, Observer } from "rxjs";
import { Viewerlayer } from "../../model/viewerlayer";
import { IPropertyResponse } from "../../interfaces/i-property-response";
import { IDamageAssessmentResponse } from "../../interfaces/i-damage-assessment-response";

@Injectable({
  providedIn: "root",
})
export class GeoJSONDatabaseService extends NgxIndexedDBService {
  saveLayer(layer: Viewerlayer): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.add("user-layers", layer).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  updateLayer(layer: Viewerlayer): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.update("user-layers", layer).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  deleteLayer(layerId: string): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.delete("user-layers", layerId).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  deleteLayers(layerIds: string[]): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.bulkDelete("user-layers", layerIds).subscribe({
          next: (msg) => {
            console.log("Layers properly deleted");
            observer.next(true);
            observer.complete();
          },
          error: (err) => {
            console.error("Error deleting layers: ", err);
            observer.error(err);
          },
        });
      } catch (error) {
        console.error("Error deleting layers: ", error);
        observer.error(error);
      }
    });
  }

  getLayers(): Observable<Viewerlayer[]> {
    return new Observable((observer: Observer<Viewerlayer[]>) => {
      try {
        this.getAll("user-layers").subscribe(
          (layers: Viewerlayer[]) => {
            observer.next(layers);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          },
        );
      } catch (error) {
        observer.error(error);
      }
    });
  }

  getUserLayers(userId: string): Observable<Viewerlayer[]> {
    return new Observable((observer: Observer<Viewerlayer[]>) => {
      try {
        this.getAll("user-layers").subscribe(
          (layers: Viewerlayer[]) => {
            observer.next(layers.filter((layer) => layer.userId === userId));
            observer.complete();
          },
          (error) => {
            observer.error(error);
          },
        );
      } catch (error) {
        observer.error(error);
      }
    });
  }

  clearUserLayers(): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.clear("user-layers").subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  deleteUserDatabase(): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.deleteDatabase().subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  saveProperty(property: IPropertyResponse): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.add("user-properties", property).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  updateProperty(property: IPropertyResponse): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.update("user-properties", property).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  deleteProperty(propertyId: string): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.delete("user-properties", propertyId).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  getProperties(): Observable<IPropertyResponse[]> {
    return new Observable((observer: Observer<IPropertyResponse[]>) => {
      try {
        this.getAll("user-properties").subscribe(
          (properties: IPropertyResponse[]) => {
            observer.next(properties);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          },
        );
      } catch (error) {
        observer.error(error);
      }
    });
  }

  savePropertyDamageAssessment(damageAssessment: IDamageAssessmentResponse): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.add("user-properties-damage-assessments", damageAssessment).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  updatePropertyDamageAssessment(damageAssessment: IDamageAssessmentResponse): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.update("property-damage-assessments", damageAssessment).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  deletePropertyDamageAssessment(damageAssessmentId: string): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.delete("property-damage-assessments", damageAssessmentId).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          () => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }

  getPropertyDamageAssessment(): Observable<IDamageAssessmentResponse[]> {
    return new Observable((observer: Observer<IDamageAssessmentResponse[]>) => {
      try {
        this.getAll("property-damage-assessments").subscribe(
          (damageAssesment: IDamageAssessmentResponse[]) => {
            observer.next(damageAssesment);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          },
        );
      } catch (error) {
        observer.error(error);
      }
    });
  }

  /**
   * Saves multiple layers to IndexedDB in a single operation.
   * @param layers Array of Viewerlayer objects
   * @returns Observable<boolean> True if all layers saved successfully
   */
  saveLayers(layers: Viewerlayer[]): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      try {
        this.bulkAdd("user-layers", layers).subscribe(
          () => {
            observer.next(true);
            observer.complete();
          },
          (error) => {
            observer.next(false);
            observer.complete();
          },
        );
      } catch (error) {
        observer.next(false);
        observer.complete();
      }
    });
  }
}
